import React, { useState, useEffect } from 'react';
import { Target, RefreshCw, AlertTriangle, Search, Activity, XOctagon, UploadCloud, Eye, ShieldAlert } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

type EntityType = 'INDIVIDUAL' | 'COMPANY' | 'GOVERNMENT';

export const RogueEntityTerminal: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'REPORT' | 'HAWK_EYE'>(() => {
    return (localStorage.getItem('nexorax_rogue_active_tab') as any) || 'REPORT';
  });
  
  // Form State
  const [entityType, setEntityType] = useState<EntityType>(() => {
    return (localStorage.getItem('nexorax_rogue_entity_type') as any) || 'INDIVIDUAL';
  });
  const [entityName, setEntityName] = useState(() => {
    return localStorage.getItem('nexorax_rogue_entity_name') || '';
  });
  const [violationType, setViolationType] = useState(() => {
    return localStorage.getItem('nexorax_rogue_violation_type') || '';
  });
  const [details, setDetails] = useState(() => {
    return localStorage.getItem('nexorax_rogue_details') || '';
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    localStorage.setItem('nexorax_rogue_active_tab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    localStorage.setItem('nexorax_rogue_entity_type', entityType);
  }, [entityType]);

  useEffect(() => {
    localStorage.setItem('nexorax_rogue_entity_name', entityName);
  }, [entityName]);

  useEffect(() => {
    localStorage.setItem('nexorax_rogue_violation_type', violationType);
  }, [violationType]);

  useEffect(() => {
    localStorage.setItem('nexorax_rogue_details', details);
  }, [details]);

  const getCategoryByViolation = (violation: string) => {
    const categories = {
      "Financial & Corporate Compliance": [
        "Unauthorized Trademark Use", "Payment Gateway Discrepancies", "Intellectual Property Extractor", 
        "Financial Data Inconsistency", "Corporate Identity Duplication", "Fraudulent Account Registration"
      ],
      "Network Safety & Security Compliance": [
        "DDoS Trace Discovery", "Data Privacy Breach Profile", "Rogue Domain Redirection", 
        "Shadow Proxy Deployment", "Critical Service Misconfiguration", "Vulnerability Exploit Scanning"
      ],
      "Social & Brand Safety Verification": [
        "Aesthetic/Asset Misrepresentation", "Disinformation Deployment", "Copyright Infringement", 
        "Deceptive Copying", "Systematic Visual Spoofing"
      ],
      "Regulatory & Policy Safeguards": [
        "Terms of Service Violation", "Sanction List Conflict", "Data Locality Compliance", 
        "Privacy Policy Discrepancy", "Registrar Expiry Risk", "Judicial Hold Tracking"
      ],
      "Operational Logistics Integrity": [
        "Inbound Resource Flow Inconsistency", "Contractual Service Level Breach", "Logistical Delivery Interruption", 
        "System Access Key Leak", "Cryptographic Anomaly Logs"
      ]
    };
    
    for (const [key, values] of Object.entries(categories)) {
      if (values.includes(violation)) {
        return key;
      }
    }
    return "Unclassified Policy Incident";
  };

  // Hawk Eye State
  const [isUpdating, setIsUpdating] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date().toISOString());
  const [threats, setThreats] = useState([
    { id: 'I-001', name: 'Synthetix Global', type: 'COMPANY', subCategory: 'Financial & Corporate Compliance', status: 'MONITORING ACTIVE', detail: 'Corporate Identity Duplication', progress: 85 },
    { id: 'I-002', name: 'Sector 4 Service Node', type: 'GOVERNMENT', subCategory: 'Regulatory & Policy Safeguards', status: 'AUDITING', detail: 'Terms of Service Violation', progress: 42 },
    { id: 'I-003', name: 'John Doe (Alias: Cipher)', type: 'INDIVIDUAL', subCategory: 'Network Safety & Security Compliance', typeSecondary: 'UNVERIFIED ENTRANT', status: 'HOLD ENFORCED', detail: 'Intellectual Property Copying', progress: 100 },
  ]);

  const handleReportSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setThreats(prev => [{
        id: `I-00${prev.length + 1}`,
        name: entityName,
        type: entityType,
        subCategory: getCategoryByViolation(violationType),
        status: 'PENDING RESOLUTON',
        detail: violationType,
        progress: 10
      }, ...prev]);
      setIsSubmitting(false);
      setSubmitSuccess(true);
      setEntityName('');
      setViolationType('');
      setDetails('');
      setTimeout(() => setSubmitSuccess(false), 3000);
      setActiveTab('HAWK_EYE');
    }, 1500);
  };

  const forceUpdate = () => {
    setIsUpdating(true);
    setTimeout(() => {
      setLastUpdate(new Date().toISOString());
      setIsUpdating(false);
      setThreats(prev => prev.map(t => {
        if (t.progress < 100) {
          return { ...t, progress: Math.min(100, t.progress + Math.floor(Math.random() * 20)) };
        }
        return t;
      }));
    }, 2000);
  };

  return (
    <div className="flex flex-col h-full bg-[#050914] text-slate-300 border border-blue-900/40 rounded-xl overflow-hidden font-mono relative shadow-[0_0_50px_-15px_rgba(59,130,246,0.15)]">
      {/* Header */}
      <div className="border-b border-blue-900/30 bg-black/60 p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded bg-blue-950/50 border border-blue-500/35 flex items-center justify-center shadow-[0_0_15px_rgba(59,130,246,0.25)]">
            <ShieldAlert className="w-6 h-6 text-blue-450" />
          </div>
          <div>
            <h2 className="text-blue-400 font-bold text-xl uppercase tracking-[0.2em] flex items-center gap-3">
              POLICY & INTEGRITY RUNTIME
              <span className="flex h-2 w-2 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
              </span>
            </h2>
            <p className="text-[10px] text-blue-400/60 uppercase tracking-widest mt-1">Submit Cases for Policy Review // Domain Integrity Monitoring Active</p>
          </div>
        </div>

        <div className="flex bg-black/50 p-1 border border-blue-900/40 rounded">
          <button 
            onClick={() => setActiveTab('REPORT')}
            className={`px-6 py-2 text-[10px] font-bold uppercase tracking-widest transition-colors ${activeTab === 'REPORT' ? 'bg-blue-900/30 text-white' : 'text-blue-500/50 hover:text-white'}`}
          >
            REPORT INCIDENT
          </button>
          <button 
            onClick={() => setActiveTab('HAWK_EYE')}
            className={`px-6 py-2 text-[10px] font-bold uppercase tracking-widest transition-colors flex items-center gap-2 ${activeTab === 'HAWK_EYE' ? 'bg-blue-900/30 text-white' : 'text-blue-500/50 hover:text-white'}`}
          >
            <Eye className="w-3 h-3" /> RESOLUTION TRACKER
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-hidden relative">
        <AnimatePresence mode="wait">
          {activeTab === 'REPORT' ? (
            <motion.div 
              key="report"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="absolute inset-0 p-8 custom-scrollbar overflow-y-auto"
            >
              <div className="max-w-3xl mx-auto">
                <div className="bg-blue-950/15 border-l-2 border-blue-500 p-4 mb-8">
                  <p className="text-[11px] text-blue-300/80 leading-relaxed uppercase tracking-widest">
                    <span className="text-blue-400 font-bold">NOTICE:</span> Incidents registered in this module are actively reviewed under the NexoraX Security & Policy Protocol. Verified compliance violations are routed to domain registrars, service providers, and legal counsel for dispute resolution and trademark protective action.
                  </p>
                </div>

                <form onSubmit={handleReportSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {(['INDIVIDUAL', 'COMPANY', 'GOVERNMENT'] as EntityType[]).map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setEntityType(type)}
                        className={`p-4 border text-[11px] font-bold uppercase tracking-widest flex flex-col items-center gap-3 transition-colors ${entityType === type ? 'bg-blue-950/40 border-blue-500 text-blue-100 shadow-[0_0_15px_rgba(59,130,246,0.1)]' : 'bg-black/40 border-blue-950/30 text-blue-400/50 hover:border-blue-500/40'}`}
                      >
                        <Target className={`w-5 h-5 ${entityType === type ? 'text-blue-400' : 'text-blue-500/50'}`} />
                        {type}
                      </button>
                    ))}
                  </div>

                  <div className="space-y-4 pt-4">
                    <div>
                      <label className="text-[10px] text-blue-400/80 uppercase tracking-widest mb-2 block">Incident Subject / Name</label>
                      <input 
                        required
                        type="text" 
                        value={entityName}
                        onChange={e => setEntityName(e.target.value)}
                        className="w-full bg-black/50 border border-blue-900/30 rounded p-4 text-sm text-white focus:outline-none focus:border-blue-500 transition-colors placeholder-blue-900/20 font-mono"
                        placeholder="ENTER CASE NAME OR INCIDENT DOMAIN MODULE..."
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-blue-400/80 uppercase tracking-widest mb-2 block">Violation Type / compliance Sub-Category</label>
                      <select 
                        required
                        value={violationType}
                        onChange={e => setViolationType(e.target.value)}
                        className="w-full bg-black/50 border border-blue-900/30 rounded p-4 text-sm text-white focus:outline-none focus:border-blue-500 transition-colors placeholder-blue-900/20 font-mono appearance-none"
                      >
                        <option value="" disabled>SELECT A SPECIFIC POLICY ISSUE CATEGORY...</option>
                        <optgroup label="Financial & Corporate Compliance">
                          <option value="Unauthorized Trademark Use">Unauthorized Trademark Use</option>
                          <option value="Payment Gateway Discrepancies">Payment Gateway Discrepancies</option>
                          <option value="Intellectual Property Extractor">Intellectual Property Extractor</option>
                          <option value="Financial Data Inconsistency">Financial Data Inconsistency</option>
                          <option value="Corporate Identity Duplication">Corporate Identity Duplication</option>
                          <option value="Fraudulent Account Registration">Fraudulent Account Registration</option>
                        </optgroup>
                        <optgroup label="Network Safety & Security Compliance">
                          <option value="DDoS Trace Discovery">DDoS Trace Discovery</option>
                          <option value="Data Privacy Breach Profile">Data Privacy Breach Profile</option>
                          <option value="Rogue Domain Redirection">Rogue Domain Redirection</option>
                          <option value="Shadow Proxy Deployment">Shadow Proxy Deployment</option>
                          <option value="Critical Service Misconfiguration">Critical Service Misconfiguration</option>
                          <option value="Vulnerability Exploit Scanning">Vulnerability Exploit Scanning</option>
                        </optgroup>
                        <optgroup label="Social & Brand Safety Verification">
                          <option value="Aesthetic/Asset Misrepresentation">Aesthetic/Asset Misrepresentation</option>
                          <option value="Disinformation Deployment">Disinformation Deployment</option>
                          <option value="Copyright Infringement">Copyright Infringement</option>
                          <option value="Deceptive Copying">Deceptive Copying</option>
                          <option value="Systematic Visual Spoofing">Systematic Visual Spoofing</option>
                        </optgroup>
                        <optgroup label="Regulatory & Policy Safeguards">
                          <option value="Terms of Service Violation">Terms of Service Violation</option>
                          <option value="Sanction List Conflict">Sanction List Conflict</option>
                          <option value="Data Locality Compliance">Data Locality Compliance</option>
                          <option value="Privacy Policy Discrepancy">Privacy Policy Discrepancy</option>
                          <option value="Registrar Expiry Risk">Registrar Expiry Risk</option>
                          <option value="Judicial Hold Tracking">Judicial Hold Tracking</option>
                        </optgroup>
                        <optgroup label="Operational Logistics Integrity">
                          <option value="Inbound Resource Flow Inconsistency">Inbound Resource Flow Inconsistency</option>
                          <option value="Contractual Service Level Breach">Contractual Service Level Breach</option>
                          <option value="Logistical Delivery Interruption">Logistical Delivery Interruption</option>
                          <option value="System Access Key Leak">System Access Key Leak</option>
                          <option value="Cryptographic Anomaly Logs">Cryptographic Anomaly Logs</option>
                        </optgroup>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] text-blue-400/80 uppercase tracking-widest mb-2 block">Factual Evidence & Coordinates</label>
                      <textarea 
                        required
                        rows={4}
                        value={details}
                        onChange={e => setDetails(e.target.value)}
                        className="w-full bg-black/50 border border-blue-900/30 rounded p-4 text-sm text-white focus:outline-none focus:border-blue-500 transition-colors placeholder-blue-900/20 font-mono resize-none"
                        placeholder="PROVIDE INFRINGEMENT FACTS, SOURCE REGISTRATION RECORD, OR LOG METRICS..."
                      ></textarea>
                    </div>
                  </div>

                  <div className="pt-4 flex justify-end">
                    <button 
                      type="submit" 
                      disabled={isSubmitting || submitSuccess}
                      className="bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] rounded flex items-center gap-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <><RefreshCw className="w-4 h-4 animate-spin" /> DISPATCHING REPORT TO POLICY DIRECTORY</>
                      ) : submitSuccess ? (
                        <><AlertTriangle className="w-4 h-4 text-yellow-300" /> EVENT PERSISTED</>
                      ) : (
                        <><UploadCloud className="w-4 h-4" /> SUBMIT FOR COMPLIANCE VERIFICATION</>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          ) : (
            <motion.div 
              key="hawk"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="absolute inset-0 p-8 custom-scrollbar overflow-y-auto flex flex-col"
            >
              <div className="flex justify-between items-end mb-8 border-b border-blue-900/20 pb-4">
                <div>
                  <h3 className="text-blue-400 font-bold text-lg uppercase tracking-widest flex items-center gap-2">
                    <Activity className="w-5 h-5" /> GATHERED INTEGRITY WORKFLOWS
                  </h3>
                  <p className="text-[10px] text-blue-400/60 uppercase tracking-widest mt-1">Status of Domain Policy Reviews and Resolution Tasks</p>
                </div>
                <div className="flex flex-col items-end gap-3">
                  <span className="text-[10px] text-slate-500 uppercase tracking-widest font-mono">Last Sync: {new Date(lastUpdate).toLocaleTimeString()}</span>
                  <button 
                    onClick={forceUpdate}
                    disabled={isUpdating}
                    className="flex items-center gap-2 border border-blue-500/30 bg-blue-950/30 hover:bg-blue-900/30 text-blue-400 px-4 py-2 text-[10px] rounded font-bold uppercase tracking-widest transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3 h-3 ${isUpdating ? 'animate-spin' : ''}`} />
                    FORCE GATHER LATEST REVIEWS
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4">
                {threats.map((threat, idx) => (
                  <div key={idx} className="bg-black/40 border border-blue-950/40 rounded p-6 shadow-lg shadow-black/50 relative overflow-hidden group">
                    <div className="absolute top-0 left-0 w-1 h-full bg-blue-600/20 group-hover:bg-blue-500 transition-colors"></div>
                    <div className="flex flex-col md:flex-row justify-between gap-6">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-3 mb-2">
                          <span className="bg-blue-950 text-blue-400 px-2 flex items-center justify-center font-mono text-[9px] font-bold tracking-widest rounded border border-blue-900/50 h-5">
                            {threat.id}
                          </span>
                          <span className="bg-blue-950/50 text-blue-400 px-2 flex items-center justify-center font-mono text-[9px] font-bold tracking-widest rounded border border-blue-900/30 h-5">
                            {threat.type}
                          </span>
                          {threat.subCategory && (
                            <span className="bg-indigo-950/35 text-indigo-400 px-2 flex items-center justify-center font-mono text-[9px] font-bold tracking-widest rounded border border-indigo-900/20 h-5">
                              {threat.subCategory}
                            </span>
                          )}
                        </div>
                        <h4 className="text-lg font-bold text-white uppercase tracking-wider mb-1 flex items-center gap-2">
                          {threat.name}
                        </h4>
                        <p className="text-xs text-slate-400 uppercase tracking-widest font-mono">
                          <span className="text-blue-400/75">POLICY VIOLATION:</span> {threat.detail}
                        </p>
                      </div>

                      <div className="flex-1 md:border-l md:border-blue-950/30 md:pl-6 flex flex-col justify-center">
                        <div className="flex justify-between items-center mb-2">
                          <span className={`text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 ${threat.progress === 100 ? 'text-emerald-400' : 'text-blue-400'}`}>
                            {threat.progress === 100 ? <XOctagon className="w-3 h-3" /> : <Search className="w-3 h-3" />}
                            {threat.status}
                          </span>
                          <span className="text-xs text-white font-mono">{threat.progress}%</span>
                        </div>
                        <div className="w-full bg-black border border-blue-950/40 h-2 rounded overflow-hidden">
                          <div 
                            className={`h-full transition-all duration-1000 ${threat.progress === 100 ? 'bg-emerald-500 shadow-[0_0_10px_#10b981]' : 'bg-blue-500 shadow-[0_0_10px_#3b82f6]'}`}
                            style={{ width: `${threat.progress}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
